import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api3005 from '../../services/api3005';
import Swal from 'sweetalert2';

/* ===== ESTILOS ===== */

const Container = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
`;

const Title = styled.h2`
  margin-bottom: 20px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 6px;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #ccc;
`;

const Select = styled.select`
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #ccc;
`;

const Button = styled.button`
  margin-top: 10px;
  padding: 8px;
  border-radius: 8px;
  border: 2px solid #007bff;
  color: #007bff;
  background: transparent;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    background-color: #007bff;
    color: white;
  }
`;

const CancelButton = styled(Button)`
  border-color: red;
  color: red;

  &:hover {
    background-color: red;
    color: white;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
`;

/* ===== FUNÇÕES MOEDA ===== */

const formatCurrency = (value) => {
  const numericValue = value.replace(/\D/g, '');
  const number = (Number(numericValue) / 100).toFixed(2);

  return Number(number).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const parseCurrencyToFloat = (value) => {
  return Number(
    value
      .replace('R$', '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim()
  );
};

/* ===== COMPONENTE ===== */

const ContasForm = ({ onClose, onCreated, conta }) => {
  const isEditing = !!conta;

  const [formData, setFormData] = useState({
    nome: '',
    categoria: '',
    valor: '',
    total_parcelas: 1,
    data_vencimento: '',
  });

  const [error, setError] = useState(null);

  useEffect(() => {
    if (conta) {
      setFormData({
        nome: conta.nome || '',
        categoria: conta.categoria || '',
        valor: Number(conta.valor).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
        total_parcelas: conta.total_parcelas || 1,
        data_vencimento: conta.data_vencimento
          ? conta.data_vencimento.split('T')[0]
          : '',
      });
    }
  }, [conta]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const limits = {
      nome: 255,
      categoria: 100
    };

    if (limits[name] && value.length > limits[name]) return;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleValorChange = (e) => {
    const formatted = formatCurrency(e.target.value);
    setFormData({
      ...formData,
      valor: formatted,
    });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  const confirmResult = await Swal.fire({
    title: isEditing
      ? 'Tem certeza que deseja editar essa conta?'
      : 'Tem certeza que deseja criar essa conta?',
    text: isEditing
      ? 'As informações serão atualizadas.'
      : 'A nova conta será criada.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#007bff',
    cancelButtonColor: '#d33',
    confirmButtonText: isEditing ? 'Sim, editar' : 'Sim, criar',
    cancelButtonText: 'Cancelar',
  });

  if (!confirmResult.isConfirmed) return;

  try {
    const token = localStorage.getItem('token');

    const payload = {
      ...formData,
      valor: parseCurrencyToFloat(formData.valor),
      total_parcelas: parseInt(formData.total_parcelas),
    };

    if (isEditing) {
      await api3005.put(
        `/api/contas-pagar/${conta.id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } else {
      await api3005.post(
        '/api/contas-pagar',
        {
          ...payload,
          numero_parcela: 1,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    }

    await Swal.fire({
      icon: 'success',
      title: isEditing
        ? 'Conta atualizada com sucesso!'
        : 'Conta criada com sucesso!',
      timer: 1500,
      showConfirmButton: false,
    });

    onCreated();
    onClose();
  } catch (error) {
    setError(
      error.response?.data?.error ||
        (isEditing
          ? 'Erro ao atualizar conta'
          : 'Erro ao criar conta')
    );
  }
};

  return (
    <Container>
      <Title>
        {isEditing ? 'Editar Conta' : 'Nova Conta'}
      </Title>

      {error && (
        <p style={{ color: 'red', textAlign: 'center' }}>
          {error}
        </p>
      )}

      <Form onSubmit={handleSubmit}>

        <Field>
          <Label>Nome da Conta</Label>
          <Input
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            required
            maxLength={255}
          />
        </Field>

        <Field>
          <Label>Categoria</Label>
          <Select
            name="categoria"
            value={formData.categoria}
            onChange={handleChange}
            required
          >
            <option value="">Selecione uma categoria</option>
            <option value="Suprimentos">Suprimentos</option>
            <option value="Produtos de limpeza">Produtos de limpeza</option>
            <option value="Manutenção">Manutenção</option>
            <option value="Serviços">Serviços</option>
            <option value="Combustível">Combustível</option>
            <option value="Débitos">Débitos</option>
          </Select>
        </Field>

        <Field>
          <Label>{isEditing ? "Valor da Parcela" : "Valor Total"}</Label>
          <Input
            type="text"
            name="valor"
            value={formData.valor}
            onChange={handleValorChange}
            placeholder="R$ 0,00"
            required
          />
        </Field>

        <Field>
          <Label>Total de Parcelas</Label>
          <Input
            type="number"
            name="total_parcelas"
            min="1"
            max="9999"
            value={formData.total_parcelas}
            onChange={handleChange}
            required
          />
        </Field>

        <Field>
          <Label>Data de Vencimento</Label>
          <Input
            type="date"
            name="data_vencimento"
            value={formData.data_vencimento}
            onChange={handleChange}
            required
          />
        </Field>

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose}>
            Cancelar
          </CancelButton>

          <Button type="submit">
            {isEditing ? 'Atualizar' : 'Salvar'}
          </Button>
        </ButtonGroup>

      </Form>
    </Container>
  );
};

export default ContasForm;